import {isFeatureEnabled} from 'feature-flags';
import {
  closeCurrentDialog,
  DropdownActionMenuItem,
  openConfirmDialog,
  openDeleteDialog,
  showNotification,
} from 'platform/components';

import {useSelector} from 'react-redux';
// eslint-disable-next-line no-restricted-imports -- [T20-75547]
import {useNavigate} from 'react-router-dom';

import {isFalse, isNotNil, isTrue} from 'ramda-adjunct';

import {useGetParticipationQuery} from '@dms/api/core';
import {
  useArchiveVehicleMutation,
  useDeleteVehicleMutation,
  useGetVehicleV2Query,
  useUnarchiveVehicleMutation,
} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {
  selectSaleVehicle,
  useActivateRentalVehicleMutation,
  useDeactivateRentalVehicleMutation,
  useGetRentalVehicleQuery,
  useMakeSaleVehicleAvailableForSaleMutation,
  useMakeSaleVehicleIsNotForSaleMutation,
  useMakeSaleVehicleIsNotSoldMutation,
  useRegisterVehicleForSaleMutation,
  useReRegisterVehicleForSaleMutation,
} from '@dms/api/vehicle';
import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';
import {testIds, vehiclesRoutes} from '@dms/routes';
import {handleApiError, usePermissions, useSaleVehicleActions} from '@dms/shared';

import {buildArray, composePath} from 'shared/web';

export const useGetSecondaryVehicleHeaderActions = (vehicleId: string) => {
  const navigate = useNavigate();

  const {data: saleVehicle} = useSelector(selectSaleVehicle(vehicleId));
  const {data: rentalVehicle} = useGetRentalVehicleQuery({vehicleId});
  const {data: vehicle} = useGetVehicleV2Query({vehicleId});
  const isArchived = isTrue(vehicle?.isArchived);
  const isSaleVehicle = isNotNil(saleVehicle);

  const {data: vehicleParticipation} = useGetParticipationQuery({
    resourceId: EntityResourceIds.vehicle,
    recordId: vehicleId,
  });

  const [
    canReregister,
    canDeleteVehicle,
    canReadBusinessCase,
    canUpdateVehicle,
    canReregisterForSale,
    canCloneSaleVehicle,
    canReadServiceCase,
    canPutUpForSale,
  ] = usePermissions({
    permissionKeys: [
      'vehicleReregisterForSale',
      'deleteVehicle',
      'businessCaseRead',
      'updateVehicle',
      'vehicleReregisterForSale',
      'cloneSaleVehicle',
      'serviceCaseRead',
      'vehiclePutUpForSale',
    ],
    scopes: {
      deleteVehicle: {
        participation: vehicleParticipation,
        branchId: saleVehicle?.branchId,
      },
      updateVehicle: {
        participation: vehicleParticipation,
        branchId: saleVehicle?.branchId,
      },
      cloneSaleVehicle: {
        participation: vehicleParticipation,
        branchId: saleVehicle?.branchId,
      },
    },
  });

  const isSalesModuleEnabled = canReadBusinessCase;
  const [makeSaleVehicleNotForSale] = useMakeSaleVehicleIsNotForSaleMutation();
  const [deleteVehicle] = useDeleteVehicleMutation();
  const [makeSaleVehicleIsNotSold] = useMakeSaleVehicleIsNotSoldMutation();
  const [makeSaleVehicleAvailableForSale] = useMakeSaleVehicleAvailableForSaleMutation();
  const [reRegisterVehicleForSale] = useReRegisterVehicleForSaleMutation();
  const [registerVehicleForSale] = useRegisterVehicleForSaleMutation();
  const [unarchiveVehicle, {isLoading: isLoadingArchiveVehicle}] = useUnarchiveVehicleMutation();
  const [archiveVehicle, {isLoading: isLoadingUnarchiveVehicle}] = useArchiveVehicleMutation();
  const [activateRentalVehicle, {isLoading: isLoadingActivateRentalVehicle}] =
    useActivateRentalVehicleMutation();
  const [deactivateRentalVehicle, {isLoading: isLoadingDeactivateRentalVehicle}] =
    useDeactivateRentalVehicleMutation();

  const {isSaleActionEnabled} = useSaleVehicleActions(vehicleId);

  const isArchiveLoading = isLoadingArchiveVehicle || isLoadingUnarchiveVehicle;
  const handleArchiveVehicle = isArchived ? unarchiveVehicle : archiveVehicle;
  const isRemoveVehicleFromSaleFlagEnabled = isFeatureEnabled(
    featureFlags.REMOVE_VEHICLE_FROM_SALE
  );

  const handleClone = () => {
    if (!isSaleVehicle && canReadServiceCase) {
      return navigate({
        pathname: composePath(vehiclesRoutes.cloneServiceVehicleV2, {
          params: {
            id: vehicleId,
          },
          isSearchParamsPreserved: false,
        }),
      });
    }

    return navigate({
      pathname: composePath(vehiclesRoutes.cloneV2, {
        params: {id: vehicleId},
        isSearchParamsPreserved: false,
      }),
    });
  };

  return buildArray<DropdownActionMenuItem>()
    .when(
      isSalesModuleEnabled &&
        isSaleActionEnabled('SALE_VEHICLE_RE_REGISTER_FOR_SALE') &&
        isSaleVehicle &&
        canReregister,
      {
        label: i18n.t('entity.vehicle.labels.reRegisterToSell'),
        onClick: () =>
          openConfirmDialog({
            onConfirm: () => reRegisterVehicleForSale({vehicleId}).unwrap().catch(handleApiError),
            text: i18n.t('entity.vehicle.labels.confirmReRegisterToSell'),
            'data-testid': testIds.vehicles.detail('vehicleHeader-ReRegisterConfirmDialog'),
          }),
      }
    )
    .when(
      isSalesModuleEnabled &&
        isSaleActionEnabled('SALE_VEHICLE_PUT_BACK_UP_FOR_SALE') &&
        isSaleVehicle &&
        canReregister,
      {
        label: i18n.t('entity.vehicle.labels.AddToSell'),
        onClick: () =>
          openConfirmDialog({
            onConfirm: async () => {
              await makeSaleVehicleAvailableForSale({vehicleId}).unwrap().catch(handleApiError);
              closeCurrentDialog();
            },
            text: i18n.t('entity.vehicle.labels.AddToSellConfirmDialogSubTitle'),
            'data-testid': testIds.vehicles.detail('vehicleHeader-AddToSaleConfirmDialog'),
          }),

        'data-testid': testIds.vehicles.detail('vehicleHeader-toSell'),
      }
    )

    .when(
      isSalesModuleEnabled &&
        isSaleActionEnabled('SALE_VEHICLE_REMOVE_FROM_SALE') &&
        isSaleVehicle &&
        isRemoveVehicleFromSaleFlagEnabled,
      {
        label: i18n.t('entity.vehicle.labels.removeFromSale'),
        onClick: () =>
          openConfirmDialog({
            onConfirm: async () => {
              await makeSaleVehicleIsNotSold({vehicleId}).unwrap().catch(handleApiError);
              closeCurrentDialog();
            },
            text: i18n.t('entity.vehicle.actions.notForSaleConfirmDialogSubTitle'),
            'data-testid': testIds.vehicles.detail('vehicleHeader-notForSaleConfirmDialog'),
          }),
        'data-testid': testIds.vehicles.detail('vehicleHeader-markNotSold'),
      }
    )
    .when(!isSaleVehicle && canReadServiceCase && canPutUpForSale, {
      label: i18n.t('entity.vehicle.actions.registerForSale'),
      onClick: () =>
        openConfirmDialog({
          onConfirm: () =>
            registerVehicleForSale({
              vehicleId,
              registerVehicleForSaleV2RequestBody: {},
            })
              .unwrap()
              .catch(handleApiError),
          text: i18n.t('entity.vehicle.actions.registerForSaleConfirmDialogSubTitle'),
          'data-testid': testIds.vehicles.detail('vehicleHeader-registerForSaleConfirmDialog'),
        }),
      'data-testid': testIds.vehicles.detail('vehicleHeader-registerForSale'),
    })
    .when(canUpdateVehicle && (!canReadServiceCase || isSaleVehicle), {
      label: isArchived
        ? i18n.t('entity.vehicle.labels.unarchive')
        : i18n.t('entity.vehicle.labels.archive'),
      onClick: () => handleArchiveVehicle({vehicleId}),
      isLoading: isArchiveLoading,
      'data-testid': testIds.vehicles.detail(
        isArchived ? 'vehicleHeader-unarchive' : 'vehicleHeader-archive'
      ),
    })
    .when(canUpdateVehicle && !isArchived && canCloneSaleVehicle, {
      label: i18n.t('entity.vehicle.labels.clone'),
      onClick: handleClone,
      'data-testid': testIds.vehicles.detail('vehicleHeader-clone'),
    })
    .when(isNotNil(rentalVehicle) && isTrue(rentalVehicle?.active), {
      label: i18n.t('entity.vehicle.actions.deactivateRental'),
      onClick: () => deactivateRentalVehicle({vehicleId}).unwrap().catch(handleApiError),
      isLoading: isLoadingDeactivateRentalVehicle,
    })
    .when(isNotNil(rentalVehicle) && isFalse(rentalVehicle?.active), {
      label: i18n.t('entity.vehicle.actions.activateRental'),
      onClick: () => activateRentalVehicle({vehicleId}).unwrap().catch(handleApiError),
      isLoading: isLoadingActivateRentalVehicle,
    })
    .when(
      isSalesModuleEnabled &&
        canReregisterForSale &&
        isSaleActionEnabled('SALE_VEHICLE_TEMPORARY_REMOVE_FROM_SALE') &&
        isSaleVehicle,
      {
        label: i18n.t('entity.vehicle.labels.markTemporarilyNotForSale'),
        onClick: () =>
          openConfirmDialog({
            onConfirm: async () => {
              await makeSaleVehicleNotForSale({vehicleId}).unwrap().catch(handleApiError);
              closeCurrentDialog();
            },
            text: i18n.t('entity.vehicle.actions.notForSaleConfirmDialogSubTitle'),
            'data-testid': testIds.vehicles.detail('vehicleHeader-notForSaleConfirmDialog'),
          }),
        'data-testid': testIds.vehicles.detail('vehicleHeader-markNotForSale'),
      }
    )
    .when(canDeleteVehicle, {
      label: i18n.t('entity.vehicle.actions.delete'),
      severity: canReadServiceCase ? 'danger' : undefined,
      onClick: () =>
        openDeleteDialog({
          onConfirm: () =>
            deleteVehicle({
              vehicleId,
            })
              .unwrap()
              .then(() => {
                showNotification.success(i18n.t('entity.vehicle.notifications.deleteSuccess'));
              })
              .then(() => navigate(vehiclesRoutes.home))
              .catch(handleApiError),
        }),
      'data-testid': testIds.vehicles.detail('vehicleHeader-delete'),
    });
};
